import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, CircularProgress, Snackbar, Alert
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE } from '../config';

function extractRows(payload) {
  if (Array.isArray(payload)) return payload || [];
  if (!payload || typeof payload !== 'object') return [];
  const candidates = ['items', 'results', 'data', 'product', 'rows', 'list'];
  for (const key of candidates) {
    const v = payload[key];
    if (Array.isArray(v)) return v;
  }
  return [];
}

const DataTableWithForm = ({
  title,
  fetchUrl,
  createUrl,
  updateUrl,
  deleteUrl,
  columns,
  formFields
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    fetchData();
  }, [fetchUrl]);

  const fetchData = async () => {
    if (!fetchUrl) {
      setData([]);
      setLoading(false);
      setError(false);
      return;
    }
    setLoading(true);
    setError(false);
    const timeout = setTimeout(() => {
      setLoading(false);
      setError(true);
    }, 45000);
    try {
      const res = await axios.get(API_BASE + fetchUrl);
      clearTimeout(timeout);
      const rows = extractRows(res.data);
      setData(rows);
      setLoading(false);
      if (rows.length === 0) {
        setSnackbar({ open: true, message: 'No records found.', severity: 'info' });
      }
    } catch (err) {
      clearTimeout(timeout);
      setError(true);
      setLoading(false);
      setSnackbar({ open: true, message: 'Failed to load data.', severity: 'error' });
    }
  };

  const handleOpen = (row = null) => {
    setForm(row || {});
    setEditing(row);
    setOpen(true);
  };

  const handleClose = () => {
    setForm({});
    setEditing(null);
    setOpen(false);
  };

  const handleChange = (name, value) => {
    setForm({ ...form, [name]: value });
  };

  const handleEventChange = (e) => {
    const { name, value } = e.target;
    handleChange(name, value);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        const id = editing.id ?? editing.uid;
        await axios.put(API_BASE + updateUrl(id), form);
      } else {
        await axios.post(API_BASE + createUrl, form);
      }
      setSnackbar({ open: true, message: 'Saved successfully.', severity: 'success' });
      fetchData();
      handleClose();
    } catch (err) {
      setSnackbar({ open: true, message: 'Save failed.', severity: 'error' });
      console.error(err);
    }
  };

  const handleDelete = async (rowId) => {
    try {
      await axios.delete(API_BASE + deleteUrl(rowId));
      setSnackbar({ open: true, message: 'Deleted successfully.', severity: 'success' });
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Delete failed.', severity: 'error' });
      console.error(err);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>{title}</Typography>
      <Button variant="contained" onClick={() => handleOpen()}>Create</Button>

      {loading && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 1 }}>Loading...</Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography color="error" variant="h6">
            ERROR. CONTENT CANNOT BE LOADED AT THIS MOMENT.
          </Typography>
          <Button onClick={fetchData} variant="outlined" sx={{ mt: 2 }}>Retry</Button>
        </Box>
      )}

      {!loading && !error && (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map(col => (
                  <TableCell key={col.field}>{col.label}</TableCell>
                ))}
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, idx) => {
                const rowKey = row.id ?? row.uid ?? idx;
                const rowIdForActions = row.id ?? row.uid;
                return (
                  <TableRow key={rowKey}>
                    {columns.map(col => {
                      const raw = row[col.field];
                      const display = col.format ? col.format(raw, row) : raw;
                      return <TableCell key={col.field}>{display}</TableCell>;
                    })}
                    <TableCell>
                      <IconButton onClick={() => handleOpen(row)}><Edit /></IconButton>
                      {rowIdForActions != null && (
                        <IconButton onClick={() => handleDelete(rowIdForActions)}><Delete /></IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit' : 'Create'} {title}</DialogTitle>
        <DialogContent>
          {formFields.map(field => (
            <Box key={field.name} sx={{ mb: 2 }}>
              {field.render ? (
                <>
                  <Typography variant="subtitle2" gutterBottom>{field.label}</Typography>
                  {field.render(form, value => handleChange(field.name, value))}
                </>
              ) : (
                <TextField
                  margin="dense"
                  type={field.type || 'text'}
                  label={field.label}
                  name={field.name}
                  fullWidth
                  value={form[field.name] ?? ''}
                  onChange={handleEventChange}
                  InputLabelProps={
                    field.type === 'date' || field.type === 'datetime-local'
                      ? { shrink: true }
                      : undefined
                  }
                />
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DataTableWithForm;


// import React, { useEffect, useState } from 'react';
// import {
//   Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
//   Paper, IconButton, Button, Dialog, DialogActions, DialogContent, DialogTitle,
//   TextField, CircularProgress, Snackbar, Alert
// } from '@mui/material';
// import { Edit, Delete } from '@mui/icons-material';
// import axios from 'axios';
// import { API_BASE } from '../config';

// function extractRows(payload) {
//   // If the whole payload is already an array
//   if (Array.isArray(payload)) return payload || [];

//   if (!payload || typeof payload !== 'object') return [];

//   // Try common keys that might contain arrays
//   const candidates = ['items', 'results', 'data', 'product', 'rows', 'list'];
//   for (const key of candidates) {
//     const v = payload[key];
//     if (Array.isArray(v)) return v;
//   }

//   // Nothing suitable found, return empty array
//   return [];
// }

// const DataTableWithForm = ({
//   title,
//   fetchUrl,          // e.g. '/zts/admin/v1.0/load/item/<uid>/'  (relative to API_BASE)
//   createUrl,
//   updateUrl,         // (id) => '/zts/.../<id>/'
//   deleteUrl,         // (id) => '/zts/.../<id>/'
//   columns,
//   formFields
// }) => {
//   const [data, setData] = useState([]);            // always an array here
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(false);
//   const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
//   const [open, setOpen] = useState(false);
//   const [form, setForm] = useState({});
//   const [editing, setEditing] = useState(null);

//   useEffect(() => {
//     fetchData();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [fetchUrl]); // refetch when the URL changes (e.g., machine selection)

//   const fetchData = async () => {
//     // If no URL (e.g., no machine selected yet), just clear table and skip
//     if (!fetchUrl) {
//       setData([]);
//       setLoading(false);
//       setError(false);
//       return;
//     }

//     setLoading(true);
//     setError(false);
//     const timeout = setTimeout(() => {
//       setLoading(false);
//       setError(true);
//     }, 45000);

//     try {
//       const res = await axios.get(API_BASE + fetchUrl);
//       clearTimeout(timeout);
//       const rows = extractRows(res.data);
//       setData(rows);
//       setLoading(false);
//       if (rows.length === 0) {
//         setSnackbar({ open: true, message: 'No records found.', severity: 'info' });
//       }
//     } catch (err) {
//       clearTimeout(timeout);
//       setError(true);
//       setLoading(false);
//       setSnackbar({ open: true, message: 'Failed to load data.', severity: 'error' });
//     }
//   };

//   const handleOpen = (row = null) => {
//     setForm(row || {});
//     setEditing(row);
//     setOpen(true);
//   };

//   const handleClose = () => {
//     setForm({});
//     setEditing(null);
//     setOpen(false);
//   };

//   const handleChange = (e) => {
//     setForm({ ...form, [e.target.name]: e.target.value });
//   };

//   const handleSave = async () => {
//     try {
//       if (editing) {
//         const id = editing.id ?? editing.uid; // support either id or uid
//         await axios.put(API_BASE + updateUrl(id), form);
//       } else {
//         await axios.post(API_BASE + createUrl, form);
//       }
//       setSnackbar({ open: true, message: 'Saved successfully.', severity: 'success' });
//       fetchData();
//       handleClose();
//     } catch (err) {
//       setSnackbar({ open: true, message: 'Save failed.', severity: 'error' });
//       console.error(err);
//     }
//   };

//   const handleDelete = async (rowId) => {
//     try {
//       await axios.delete(API_BASE + deleteUrl(rowId));
//       setSnackbar({ open: true, message: 'Deleted successfully.', severity: 'success' });
//       fetchData();
//     } catch (err) {
//       setSnackbar({ open: true, message: 'Delete failed.', severity: 'error' });
//       console.error(err);
//     }
//   };

//   return (
//     <Box>
//       <Typography variant="h5" sx={{ mb: 2 }}>{title}</Typography>
//       <Button variant="contained" onClick={() => handleOpen()}>Create</Button>

//       {loading && (
//         <Box sx={{ mt: 3, textAlign: 'center' }}>
//           <CircularProgress />
//           <Typography sx={{ mt: 1 }}>Loading...</Typography>
//         </Box>
//       )}

//       {error && (
//         <Box sx={{ mt: 3, textAlign: 'center' }}>
//           <Typography color="error" variant="h6">
//             ERROR. CONTENT CANNOT BE LOADED AT THIS MOMENT.
//           </Typography>
//           <Button onClick={fetchData} variant="outlined" sx={{ mt: 2 }}>Retry</Button>
//         </Box>
//       )}

//       {!loading && !error && (
//         <TableContainer component={Paper} sx={{ mt: 2 }}>
//           <Table>
//             <TableHead>
//               <TableRow>
//                 {columns.map(col => (
//                   <TableCell key={col.field}>{col.label}</TableCell>
//                 ))}
//                 <TableCell>Action</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.map((row, idx) => {
//                 const rowKey = row.id ?? row.uid ?? idx;
//                 const rowIdForActions = row.id ?? row.uid; // prefer id, else uid
//                 return (
//                   <TableRow key={rowKey}>
//                     {columns.map(col => {
//                       const raw = row[col.field];
//                       const display = col.format ? col.format(raw, row) : raw;
//                       return <TableCell key={col.field}>{display}</TableCell>;
//                     })}
//                     <TableCell>
//                       <IconButton onClick={() => handleOpen(row)}><Edit /></IconButton>
//                       {rowIdForActions != null && (
//                         <IconButton onClick={() => handleDelete(rowIdForActions)}><Delete /></IconButton>
//                       )}
//                     </TableCell>
//                   </TableRow>
//                 );
//               })}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       )}

//       <Dialog open={open} onClose={handleClose}>
//         <DialogTitle>{editing ? 'Edit' : 'Create'} {title}</DialogTitle>
//         <DialogContent>
//           {formFields.map(field => (
//             <TextField
//               key={field.name}
//               margin="dense"
//               type={field.type || 'text'}
//               label={field.label}
//               name={field.name}
//               fullWidth
//               value={form[field.name] ?? ''}
//               onChange={handleChange}
//               InputLabelProps={
//                 field.type === 'date' || field.type === 'datetime-local'
//                   ? { shrink: true }
//                   : undefined
//               }
//             />
//           ))}
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={handleClose}>Cancel</Button>
//           <Button onClick={handleSave}>{editing ? 'Update' : 'Create'}</Button>
//         </DialogActions>
//       </Dialog>

//       <Snackbar
//         open={snackbar.open}
//         autoHideDuration={4000}
//         onClose={() => setSnackbar({ ...snackbar, open: false })}
//         anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
//       >
//         <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
//           {snackbar.message}
//         </Alert>
//       </Snackbar>
//     </Box>
//   );
// };

// export default DataTableWithForm;



